import axios, { AxiosError } from 'axios';
import { ActivityAction, ActivityResourceName } from '../interfaces/activities';
import { CommonRootState } from '../combineReducers';
import { DeepReadonly } from 'utility-types';
import { IUserInfo, IUserInfoScope, UserInfoActivitiesType } from '../interfaces/userInfo';
import { sendError } from '../sendError';
import { ThunkAction } from 'redux-thunk';

const { Global } = UserInfoActivitiesType;

export const fetchUserInfoRequest = () =>
  ({
    type: 'FETCH_USERINFO_REQUEST'
  } as const);

export const hasPermission = (
  userInfo: DeepReadonly<IUserInfo> | null,
  action: ActivityAction,
  resourceName: ActivityResourceName,
  additionalScopes: IUserInfoScope[] = []
) =>
  userInfo
    ? [
        {
          id: null,
          type: Global
        } as IUserInfoScope
      ]
        .concat(additionalScopes)
        .some(({ id: findId, type: findType }) => {
          const foundScope = userInfo.activities.find(({ id, type }) => id === findId && type === findType);
          return (
            foundScope !== undefined &&
            foundScope.activities[resourceName] !== undefined &&
            foundScope.activities[resourceName]!.includes(action)
          );
        })
    : false;

export const fetchUserInfoSuccess = (payload: IUserInfo) =>
  ({
    type: 'FETCH_USERINFO_SUCCESS',
    payload
  } as const);

export const fetchUserInfoFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: 'FETCH_USERINFO_FAILURE',
    error
  } as const;
};

// Module-scoped in-flight promise so concurrent callers of fetchUserInfoIfNeeded
// share the same fetch instead of either duplicating it or (per the previous
// implementation) resolving immediately with undefined when isFetching=true.
let inflight: Promise<IUserInfo> | null = null;

const fetchUserInfo = (): ThunkAction<Promise<IUserInfo>, CommonRootState, null, Actions> => async dispatch => {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      dispatch(fetchUserInfoRequest());
      const response = await axios.get<IUserInfo>('/api/v3/userInfo');
      dispatch(fetchUserInfoSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(fetchUserInfoFailure(error));
      throw error;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
};

export const fetchUserInfoIfNeeded = (): ThunkAction<Promise<IUserInfo>, CommonRootState, null, Actions> => async (
  dispatch,
  getState
) => {
  const { userInfo, error } = getState().userInfo;
  if (userInfo) return userInfo;
  if (error) return undefined as any; // preserve original behavior: don't retry after an error
  return dispatch(fetchUserInfo());
};

export type Actions =
  | ReturnType<typeof fetchUserInfoRequest>
  | ReturnType<typeof fetchUserInfoSuccess>
  | ReturnType<typeof fetchUserInfoFailure>;
